import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import Icon from '@commons/components/Icon';
import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import { breakpoints } from '@commons/contexts/responsive';
import { Link } from '@commons/i18n/navigation';
import { API_WEBSITE } from '@commons/utils/constants';

import type { MemberNodeProps } from '..';
import styles from './index.module.scss';

/**
 * Type definition for the shared member profile component properties.
 */

type MemberProfileProps = MemberNodeProps & {
  insights?: ReactNode;
};

/**
 * Next.js Image `sizes` property for the member profile component.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/image#sizes
 */

const HEADSHOT_IMAGE_SIZES = `(max-width: ${breakpoints.md}px) 100vw, (max-width: ${breakpoints.xl}) 640px, 880px`;

/**
 * MemberProfile component.
 *
 * Renders a member's profile information (headshot, role, position, location, bio).
 * Used in different contexts, such as the expert's page and intercepted route drawer.
 */

const MemberProfile = (props: MemberProfileProps) => {
  const t = useTranslations('nodes.member');

  const { title, role, bio, position, location, headshot, insights, linkedIn } = props;

  const hasRole = role && role.length > 0;

  const renderedLocation = location && (
    <Flex align="center" border="bottom" paddingTop="md" paddingBottom="md">
      <Flex.Item width={6}>
        <Text level="label-lg" transform="uppercase" marginBottom="none">
          {t('basedIn')}
        </Text>
      </Flex.Item>
      <Flex.Item width={6}>
        <Text marginBottom="none">{location}</Text>
      </Flex.Item>
    </Flex>
  );

  const linkedInAriaLabel = linkedIn?.text?.trim() || `${title} LinkedIn profile`;

  const renderedLinkedIn = linkedIn?.url && (
    <Link href={linkedIn.url} aria-label={linkedInAriaLabel}>
      <Icon type="linkedin" fill="primary" aria-hidden="true" />
    </Link>
  );

  return (
    <Flex gap="md" direction={{ xs: 'column', lg: 'row' }}>
      <Flex.Item width={{ xs: 12, lg: 6 }}>
        <DrupalImage {...headshot} sizes={HEADSHOT_IMAGE_SIZES} />
      </Flex.Item>
      <Flex.Item width={{ xs: 12, lg: 6 }} border={{ lg: 'left' }} paddingLeft={{ lg: 'md' }}>
        <Flex gap="sm" border="bottom" direction="column" paddingBottom="md">
          {hasRole && (
            <Text level="label-lg" transform="uppercase" marginBottom="none">
              {role[0].label}
            </Text>
          )}
          <Text
            as="h1"
            level="h2"
            transform={API_WEBSITE === 'aiia' ? undefined : 'uppercase'}
            marginBottom="none"
          >
            {title}
          </Text>
          <RichText value={position} className={styles.position} />
          <RichText value={bio} />
          {renderedLinkedIn}
        </Flex>
        {renderedLocation}
        {insights}
      </Flex.Item>
    </Flex>
  );
};

export default MemberProfile;
